import {
  BackSide,
  BufferGeometry,
  LineBasicMaterial,
  type Material,
  Mesh,
  MeshStandardMaterial,
  type MeshPhysicalMaterial,
  type Object3D,
  type Texture,
} from '@seddi/three';

import { clone as cloneFabricMaterial } from '../materials/fabricMaterial';
import type {
  GarmentOcclusionGfx,
  GarmentOcclusionTextureInfo,
} from '../resources/garmentOcclusionTypes';
import type { ResourcesContext } from '../resources/resourcesContext';

/**
 * Virtual piece interface allows us to handle common functionality for pieces and seam tapes.
 */
export interface VirtualPieceRenderable {
  fabricRef?: string;
  garmentGeometryRef?: string;
  readonly id: string;
  readonly mesh: Mesh;
  occlusionRef?: string;
  readonly parent: Object3D;
}

// ------ PLACEHOLDERS ------

// Placeholders are defined in global context for now.
export const geometryPlaceholder = new BufferGeometry();
export const materialPlaceholder0 = new MeshStandardMaterial({ color: '#C4C4C4' });
export const materialPlaceholder1 = new MeshStandardMaterial({ color: '#C4C4C4' });
materialPlaceholder1.side = BackSide;
export const wireframeMaterial = new LineBasicMaterial({ color: 0x007722 });

// ------ FABRIC ------

export const attachFabric = (
  self: VirtualPieceRenderable,
  fabricId: string,
  resources: ResourcesContext,
) => {
  if (self.fabricRef) {
    detachFabric(self, resources);
  }

  // Create material instances.
  const fabric = resources.getRefFabric(fabricId);
  const material0 = cloneFabricMaterial(fabric.back || fabric.front);
  const material1 = cloneFabricMaterial(fabric.front);
  material0.side = BackSide;
  const materials = self.mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[1].dispose();
  materials[0] = material0;
  materials[1] = material1;
  self.fabricRef = fabricId;

  if (self.occlusionRef) {
    const gfx = resources.findGarmentOcclusion(self.occlusionRef);
    if (gfx) applyOcclusion(self, gfx);
  }
};

export const detachFabric = (self: VirtualPieceRenderable, resources: ResourcesContext) => {
  if (!self.fabricRef) return;

  // TODO: Unsure if this is needed or material dispose accounts for maps dispose.
  if (self.occlusionRef) {
    clearOcclusion(self);
  }

  const materials = self.mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[1].dispose();
  materials[0] = materialPlaceholder0.clone();
  materials[1] = materialPlaceholder1.clone();
  resources.retRefFabric(self.fabricRef);
  delete self.fabricRef;
};

// ------ GARMENT OCCLUSION ------

export function attachGarmentOcclusion(
  self: VirtualPieceRenderable,
  resource: GarmentOcclusionGfx,
  resources: ResourcesContext,
) {
  if (self.occlusionRef) {
    detachGarmentOcclusion(self, resources);
  }

  // NOTE: For now We do not track references at the piece level.
  self.occlusionRef = resource.id;

  if (self.fabricRef) {
    applyOcclusion(self, resource);
  }
}

export function detachGarmentOcclusion(self: VirtualPieceRenderable, resources: ResourcesContext) {
  if (!self.occlusionRef) return;

  if (self.fabricRef) {
    clearOcclusion(self);
  }

  delete self.occlusionRef;
}

function applyOcclusionToMaterial(
  material: MeshPhysicalMaterial,
  texture: Texture,
  info: GarmentOcclusionTextureInfo,
) {
  const aoMap = texture.clone();
  aoMap.matrix.copy(info.transform);
  aoMap.matrixAutoUpdate = false;
  material.aoMap = aoMap;
  material.aoMapIntensity = info.strength ?? 1;
  material.needsUpdate = true;
}

function applyOcclusion(self: VirtualPieceRenderable, gfx: GarmentOcclusionGfx) {
  const materials = self.mesh.material as unknown as MeshPhysicalMaterial[];
  const bkInfo = gfx.textureInfos[`${self.id}:bk`];
  const frInfo = gfx.textureInfos[`${self.id}:fr`];
  if (bkInfo) applyOcclusionToMaterial(materials[0], gfx.textureBk, bkInfo);
  if (frInfo) applyOcclusionToMaterial(materials[1], gfx.textureFr, frInfo);
}

function clearOcclusion(self: VirtualPieceRenderable) {
  const materials = self.mesh.material as unknown as MeshPhysicalMaterial[];
  for (const material of [materials[0], materials[1]]) {
    if (!material.aoMap) continue;
    material.aoMap.dispose();
    material.aoMap = null;
    material.aoMapIntensity = 1;
    material.needsUpdate = true;
  }
}
